import { useEffect } from "react";

function setMeta(selector, value) {
  let element = document.head.querySelector(selector);
  if (!value) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement("meta");
    const [attribute, rawValue] = selector
      .replace(/^meta\[/, "")
      .replace(/\]$/, "")
      .split("=");
    element.setAttribute(attribute, rawValue.replace(/"/g, ""));
    document.head.appendChild(element);
  }
  element.setAttribute("content", value);
}

function setCanonical(url) {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
}

function setSchema(schemaJson) {
  let script = document.head.querySelector("script[data-iwmc-schema]");
  if (!schemaJson) {
    script?.remove();
    return;
  }
  if (!script) {
    script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.setAttribute("data-iwmc-schema", "true");
    document.head.appendChild(script);
  }
  script.textContent = schemaJson;
}

/**
 * Lightweight document head manager — updates <title>, <meta name="description">,
 * canonical URL, Open Graph / Twitter tags, and (optionally) a JSON-LD schema
 * block without adding a dependency.
 *
 * Pass `schema` (object or array of objects) to inject a <script type="application/ld+json">
 * into the head. The script is keyed by `data-iwmc-schema` so subsequent renders
 * replace rather than duplicate.
 */
export default function useDocumentMeta({ title, description, image, url, schema }) {
  const schemaJson = schema ? JSON.stringify(schema) : "";

  useEffect(() => {
    if (title) document.title = title;
    const pageUrl = url || window.location.href;
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:image"]', image);
    setMeta('meta[property="og:url"]', pageUrl);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="twitter:image"]', image);
    setCanonical(pageUrl);
    setSchema(schemaJson);
  }, [description, image, schemaJson, title, url]);
}
